import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useScope } from "@/hooks/use-scope";
import { useInventoryRealtime } from "@/hooks/use-inventory-realtime";
import { getStateBalances, INV_STATES, type InvState } from "@/lib/inventory-engine.functions";
import { StateBucket } from "@/components/inventory/state-bucket-card";
import { CustodyTimeline } from "@/components/inventory/custody-timeline";

export const Route = createFileRoute("/_authenticated/app/inventory/state")({ component: StatePage });

function StatePage() {
  const { tenantId, facilityId, customerId } = useScope();
  const fn = useServerFn(getStateBalances);
  const [filter, setFilter] = useState<InvState | null>(null);

  const { data } = useQuery({
    queryKey: ["state-balances", tenantId, facilityId, customerId],
    enabled: !!tenantId,
    queryFn: () => fn({ data: { tenantId: tenantId!, facilityId: facilityId ?? null, customerId: customerId ?? null, skuId: null, groupBy: "none" } }),
    placeholderData: keepPreviousData,
  });
  useInventoryRealtime(tenantId, [["state-balances", tenantId, facilityId, customerId], ["custody-timeline", tenantId, "tenant", undefined, 100]]);

  const totals = data?.totals;
  const onHand = data?.onHand ?? 0;

  const filteredRows = useMemo(() => {
    const rows = (data?.rows ?? []) as any[];
    return filter ? rows.filter((r) => r.state === filter) : rows;
  }, [data, filter]);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-baseline justify-between">
        <div>
          <h2 className="text-lg font-semibold">Inventory State</h2>
          <p className="text-sm text-muted-foreground">On Hand <span className="font-semibold text-foreground">{onHand.toLocaleString()}</span> across all states. Click a bucket to filter.</p>
        </div>
        {filter && <button className="text-xs text-muted-foreground underline" onClick={() => setFilter(null)}>clear filter</button>}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {INV_STATES.map((s) => (
          <StateBucket key={s} state={s} qty={totals?.[s] ?? 0}
            active={filter === s} onClick={() => setFilter((f) => f === s ? null : s)} />
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <h3 className="text-sm font-medium mb-2">Detail rows{filter ? ` · ${filter}` : ""}</h3>
          <div className="border rounded-md bg-card max-h-[500px] overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 sticky top-0">
                <tr><th className="text-left px-3 py-2">SKU</th><th className="text-left px-3 py-2">Loc</th><th className="text-left px-3 py-2">Lot</th><th className="text-left px-3 py-2">State</th><th className="text-right px-3 py-2">Qty</th></tr>
              </thead>
              <tbody>
                {filteredRows.slice(0, 200).map((r, i) => (
                  <tr key={i} className="border-t">
                    <td className="px-3 py-1.5 font-mono text-xs">{r.sku_id?.slice(0, 8)}</td>
                    <td className="px-3 py-1.5 font-mono text-xs">{r.location_id?.slice(0, 8)}</td>
                    <td className="px-3 py-1.5 text-xs">{r.lot || "—"}</td>
                    <td className="px-3 py-1.5 capitalize">{r.state.replace("_", " ")}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{r.qty}</td>
                  </tr>
                ))}
                {filteredRows.length === 0 && (<tr><td colSpan={5} className="text-center text-muted-foreground py-6">No rows</td></tr>)}
              </tbody>
            </table>
          </div>
        </div>
        <div>
          <h3 className="text-sm font-medium mb-2">Recent Activity</h3>
          {tenantId && <CustodyTimeline tenantId={tenantId} entity="tenant" limit={50} />}
        </div>
      </div>
    </div>
  );
}
